import { useState } from "react";
import {
  User,
  Mail,
  Plus,
  Pencil,
  Trash2,
  X,
  Crown,
  Wrench,
  Eye,
  MessageSquare,
  HelpCircle,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { OpportunityContact } from "@/types/pipeline";
import {
  useAddOpportunityContact,
  useUpdateOpportunityContact,
  useDeleteOpportunityContact,
} from "@/hooks/useOpportunityDetail";
import { AccountContactPicker } from "@/components/pipeline/AccountContactPicker";
import { toast } from "sonner";

const BUYING_ROLES = [
  { value: "economic", label: "Economic Buyer", icon: Crown, color: "text-amber-600 bg-amber-50" },
  { value: "user", label: "User Buyer", icon: Eye, color: "text-blue-600 bg-blue-50" },
  { value: "technical", label: "Technical Buyer", icon: Wrench, color: "text-purple-600 bg-purple-50" },
  { value: "coach", label: "Coach", icon: MessageSquare, color: "text-green-600 bg-green-50" },
  { value: "champion", label: "Champion", icon: Crown, color: "text-rose-600 bg-rose-50" },
  { value: "unknown", label: "Unknown", icon: HelpCircle, color: "text-gray-600 bg-gray-50" },
] as const;

const RELATIONSHIP_STRENGTHS = [
  { value: "cold", label: "Cold", color: "bg-blue-100 text-blue-700" },
  { value: "warm", label: "Warm", color: "bg-amber-100 text-amber-700" },
  { value: "strong", label: "Strong", color: "bg-green-100 text-green-700" },
  { value: "champion", label: "Champion", color: "bg-purple-100 text-purple-700" },
] as const;

interface OpportunityContactsTabProps {
  opportunityId: string;
  contacts: OpportunityContact[];
  mainContactName: string | null;
  mainContactEmail: string | null;
  accountId?: string | null;
}

const emptyForm: {
  contact_id: string | null;
  contact_name: string;
  contact_title: string;
  contact_email: string;
  buying_role: OpportunityContact['buying_role'];
  relationship_strength: OpportunityContact['relationship_strength'];
  notes: string;
  is_primary: boolean;
} = {
  contact_id: null,
  contact_name: "",
  contact_title: "",
  contact_email: "",
  buying_role: "unknown",
  relationship_strength: "cold",
  notes: "",
  is_primary: false,
};

export function OpportunityContactsTab({
  opportunityId,
  contacts,
  mainContactName,
  mainContactEmail,
  accountId,
}: OpportunityContactsTabProps) {
  const addContact = useAddOpportunityContact();
  const updateContact = useUpdateOpportunityContact();
  const deleteContact = useDeleteOpportunityContact();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);

  const openAdd = () => {
    setForm(emptyForm);
    setEditingId(null);
    setIsAddOpen(true);
  };

  const openEdit = (contact: OpportunityContact) => {
    setForm({
      contact_id: contact.contact_id || null,
      contact_name: contact.contact_name,
      contact_title: contact.contact_title || "",
      contact_email: contact.contact_email || "",
      buying_role: contact.buying_role || "unknown",
      relationship_strength: contact.relationship_strength || "cold",
      notes: contact.notes || "",
      is_primary: contact.is_primary,
    });
    setEditingId(contact.id);
    setIsAddOpen(true);
  };

  const handleSave = () => {
    if (!form.contact_name.trim()) {
      toast.error("Contact name is required");
      return;
    }

    if (editingId) {
      updateContact.mutate(
        {
          id: editingId,
          opportunity_id: opportunityId,
          ...form,
        },
        {
          onSuccess: () => {
            toast.success("Contact updated");
            setIsAddOpen(false);
          },
          onError: () => toast.error("Failed to update contact"),
        }
      );
    } else {
      addContact.mutate(
        {
          opportunity_id: opportunityId,
          contact_id: form.contact_id || null,
          ...form,
        },
        {
          onSuccess: () => {
            toast.success("Contact added");
            setIsAddOpen(false);
          },
          onError: () => toast.error("Failed to add contact"),
        }
      );
    }
  };

  const handleDelete = (id: string) => {
    deleteContact.mutate(
      { id, opportunityId },
      {
        onSuccess: () => toast.success("Contact removed"),
        onError: () => toast.error("Failed to remove contact"),
      }
    );
  };

  const getRoleConfig = (role: string | null) =>
    BUYING_ROLES.find((r) => r.value === role) || BUYING_ROLES[4];

  const getStrengthConfig = (strength: string | null) =>
    RELATIONSHIP_STRENGTHS.find((r) => r.value === strength) || RELATIONSHIP_STRENGTHS[0];

  const hasMainContact = mainContactName && contacts.length === 0;

  return (
    <div className="space-y-4">
      {/* Header with add button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">
            Contacts & Buying Roles
          </h3>
          <p className="text-xs text-muted-foreground">
            Track who's involved and their role in the decision
          </p>
        </div>
        <div className="flex gap-2">
          <AccountContactPicker
            accountId={accountId}
            onSelect={(contact) => {
              setForm({
                ...emptyForm,
                contact_id: contact.id,
                contact_name: contact.full_name || "",
                contact_title: contact.job_title || "",
                contact_email: contact.email || "",
              });
              setEditingId(null);
              setIsAddOpen(true);
            }}
            onManualAdd={openAdd}
          />
          <Button size="sm" variant="outline" onClick={openAdd}>
            <Plus className="h-4 w-4 mr-1" />
            Add Manual
          </Button>
        </div>
      </div>

      {/* Main contact hint */}
      {hasMainContact && (
        <Card className="border-dashed">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-muted">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium">{mainContactName}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {mainContactEmail && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {mainContactEmail}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setForm({
                    ...emptyForm,
                    contact_name: mainContactName || "",
                    contact_email: mainContactEmail || "",
                    is_primary: true,
                  });
                  setEditingId(null);
                  setIsAddOpen(true);
                }}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add with Role
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 italic">
              This contact is from the opportunity record. Add them with a buying role to start tracking.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {contacts.length === 0 && !hasMainContact && (
        <Card>
          <CardContent className="py-12 text-center">
            <User className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No contacts tracked yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Add contacts to map buying roles and track relationships
            </p>
          </CardContent>
        </Card>
      )}

      {/* Contact cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {contacts.map((contact) => {
          const roleConfig = getRoleConfig(contact.buying_role);
          const strengthConfig = getStrengthConfig(contact.relationship_strength);
          const RoleIcon = roleConfig.icon;

          return (
            <Card key={contact.id} className="group">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`p-2 rounded-lg shrink-0 ${roleConfig.color}`}>
                      <RoleIcon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">
                          {contact.contact_name}
                        </p>
                        {contact.is_primary && (
                          <Badge variant="secondary" className="text-[10px] px-1.5">
                            Primary
                          </Badge>
                        )}
                      </div>
                      {contact.contact_title && (
                        <p className="text-xs text-muted-foreground truncate">
                          {contact.contact_title}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-[10px] px-1.5">
                          {roleConfig.label}
                        </Badge>
                        <Badge
                          variant="outline"
                          className={`text-[10px] px-1.5 ${strengthConfig.color}`}
                        >
                          {strengthConfig.label}
                        </Badge>
                      </div>
                      {contact.contact_email && (
                        <a
                          href={`mailto:${contact.contact_email}`}
                          className="flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                        >
                          <Mail className="h-3 w-3" />
                          {contact.contact_email}
                        </a>
                      )}
                      {contact.notes && (
                        <p className="text-xs text-muted-foreground mt-2 italic">
                          {contact.notes}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => openEdit(contact)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(contact.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Edit Contact" : "Add Contact"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.contact_name}
                onChange={(e) =>
                  setForm({ ...form, contact_name: e.target.value })
                }
                placeholder="e.g., Sarah Chen"
              />
            </div>
            <div>
              <Label>Title</Label>
              <Input
                value={form.contact_title}
                onChange={(e) =>
                  setForm({ ...form, contact_title: e.target.value })
                }
                placeholder="e.g., VP Business Development"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.contact_email}
                onChange={(e) =>
                  setForm({ ...form, contact_email: e.target.value })
                }
                placeholder="sarah@company.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Buying Role</Label>
                <Select
                  value={form.buying_role}
                  onValueChange={(v) =>
                    setForm({ ...form, buying_role: v as typeof form.buying_role })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUYING_ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Relationship</Label>
                <Select
                  value={form.relationship_strength}
                  onValueChange={(v) =>
                    setForm({
                      ...form,
                      relationship_strength: v as typeof form.relationship_strength,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RELATIONSHIP_STRENGTHS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Key concerns, what they care about..."
                rows={2}
              />
            </div>
            <Button
              onClick={handleSave}
              disabled={addContact.isPending || updateContact.isPending}
              className="w-full"
            >
              {editingId ? "Update Contact" : "Add Contact"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
